import { v4 as uuidv4 } from 'uuid'

import { CustomerCardType } from '@models/commerce/CustomerCardType'
import { Currency } from '@models/commerce/Price'
import ApiRoutes from '@utils/api-routes'
import errorEmitter from '@utils/emitters/ErrorEmitter'
import http from '@utils/http'

export default class CommerceService {
  // Required to prevent duplicate charges.
  static generateIdempotencyKey() {
    return uuidv4()
  }

  static async stripeClientSecret(
    amount: number,
    currency: Currency.USD
  ): Promise<string | undefined> {
    const [resp, err] = await http.post(ApiRoutes.apiStripePaymentIntents(), { amount, currency })
    if (resp?.data) {
      return resp.data.client_secret
    } else if (err) {
      throw err
    }
  }

  static async createCustomerCard(
    token: string,
    saveCard: boolean
  ): Promise<CustomerCardType | undefined> {
    const [resp, err] = await http.post(ApiRoutes.apiCreateCustomerCard(), {
      token: token,
      save: saveCard,
    })
    if (resp?.data) {
      return resp.data.card
    } else if (err) {
      throw err
    }
  }

  static async getCustomerCards(): Promise<CustomerCardType[] | undefined> {
    const [resp, err] = await http.post(ApiRoutes.apiRetrieveCustomerCards())
    if (err) throw err
    return resp?.data?.cards ?? []
  }

  static async purchaseProduct(params) {
    const [resp, errors] = await http.post(ApiRoutes.apiPurchaseProduct(), params, {
      hideGlobalError: true,
    })

    if (!errors?.length) {
      return [resp?.data]
    }

    const errorCode = `errors.${errors[0].code}`

    // detailed error messages that explain why a purchase failed will appear in a dialog
    // with a prompt to try again
    const verboseErrors = [
      'errors.generalPaymentProcessorError',
      'errors.productNotIssued',
      'errors.duplicateTransaction',
    ]

    if (verboseErrors.indexOf(errorCode) > -1) {
      return [resp?.data, errorCode]
    }

    // other possible errors from this endpoint may be raised from Stripe
    // due to invalid credit card info, in which case the error Toast displays and
    // the user remains on the card form
    errorEmitter.addErrors(errors)
    throw new Error(errors[0].code) // these will be caught to reset the purchase dialog uiState
  }
}
